import { useEffect, useMemo, useRef, useState } from "react";
import { ArrowLeft, CircleAlert, CircleCheckBig, HelpCircle, LifeBuoy, Search, Sparkles, Upload } from "lucide-react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/hooks/useAuth";
import { useHousehold } from "@/hooks/useHousehold";
import { supabase } from "@/integrations/supabase/client";
import {
  buildReferenceCode,
  deriveAccountRoleContext,
  FAQ_ITEMS,
  getBrowserLabel,
  getDeviceType,
  matchesFaq,
  sanitizeAttachmentName,
  supportCategoryOptions,
  supportPriorityOptions,
  SUPPORT_ACTIONS,
  supportTicketSchema,
  SUPPORT_STATUS_LABELS,
  type SupportCategory,
  type SupportStatus,
} from "@/lib/support-center";
import { cn } from "@/lib/utils";
import HelpCenterSection from "@/components/support/HelpCenterSection";
import PageShell from "@/components/premium/PageShell";

type SupportTicketRow = {
  id: string;
  subject: string;
  category: SupportCategory;
  status: SupportStatus;
  priority: string;
  created_at: string;
  updated_at: string;
  message: string;
  attachment_urls: string[];
  page_path: string | null;
};

type SupportUpdateRow = {
  id: string;
  ticket_id: string;
  message: string | null;
  status: SupportStatus | null;
  created_at: string;
  visible_to_user: boolean;
};

const statusStyles: Record<SupportStatus, string> = {
  submitted: "border-primary/30 bg-primary/10 text-primary",
  in_review: "border-accent/30 bg-accent/10 text-accent",
  waiting_on_user: "border-secondary/30 bg-secondary/10 text-secondary",
  resolved: "border-border/80 bg-card text-foreground",
};

const SupportCenter = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const formRef = useRef<HTMLDivElement | null>(null);
  const { user } = useAuth();
  const { household, isParent } = useHousehold();

  const [faqQuery, setFaqQuery] = useState("");
  const [attachment, setAttachment] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [successReference, setSuccessReference] = useState<string | null>(null);
  const [tickets, setTickets] = useState<SupportTicketRow[]>([]);
  const [latestUpdates, setLatestUpdates] = useState<Record<string, SupportUpdateRow>>({});
  const [loadingTickets, setLoadingTickets] = useState(true);
  const [formState, setFormState] = useState({
    category: (searchParams.get("category") as SupportCategory | null) || "technical_issue",
    subject: "",
    message: "",
    priority: "normal",
    courseId: "",
    lessonId: "",
    moduleKey: "",
  });

  const faqResults = useMemo(() => FAQ_ITEMS.filter((item) => matchesFaq(item, faqQuery)), [faqQuery]);

  const loadTickets = async () => {
    if (!user) return;
    setLoadingTickets(true);

    const { data, error } = await supabase
      .from("support_tickets")
      .select("id, subject, category, status, priority, created_at, updated_at, message, attachment_urls, page_path")
      .eq("user_id", user.id)
      .order("created_at", { ascending: false })
      .limit(12);

    if (error) {
      setLoadingTickets(false);
      toast.error("Unable to load your support requests.");
      return;
    }

    const supportRows = (data || []) as SupportTicketRow[];
    setTickets(supportRows);

    if (supportRows.length === 0) {
      setLatestUpdates({});
      setLoadingTickets(false);
      return;
    }

    const { data: updates } = await supabase
      .from("support_ticket_updates")
      .select("id, ticket_id, message, status, created_at, visible_to_user")
      .in("ticket_id", supportRows.map((ticket) => ticket.id))
      .eq("visible_to_user", true)
      .order("created_at", { ascending: false });

    const updateMap: Record<string, SupportUpdateRow> = {};
    ((updates || []) as SupportUpdateRow[]).forEach((update) => {
      if (!updateMap[update.ticket_id]) {
        updateMap[update.ticket_id] = update;
      }
    });

    setLatestUpdates(updateMap);
    setLoadingTickets(false);
  };

  useEffect(() => {
    void loadTickets();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: effect is keyed to identity/mount only; the omitted values are render-unstable inputs and including them would re-run the fetch/side effect on every render
  }, [user]);

  useEffect(() => {
    const category = searchParams.get("category") as SupportCategory | null;
    if (category) {
      setFormState((current) => ({ ...current, category }));
    }
  }, [searchParams]);

  const handleAction = (category?: SupportCategory) => {
    if (category) {
      setFormState((current) => ({ ...current, category }));
      formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }
    navigate("/feedback");
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!user) {
      toast.error("You need to be signed in to contact support.");
      return;
    }

    const parsed = supportTicketSchema.safeParse(formState);
    if (!parsed.success) {
      toast.error(parsed.error.issues[0]?.message || "Please review your support request.");
      return;
    }

    setSubmitting(true);

    let attachmentPaths: string[] = [];
    if (attachment) {
      const safeName = sanitizeAttachmentName(attachment.name);
      const path = `${user.id}/support/${Date.now()}-${safeName}`;
      const { error: uploadError } = await supabase.storage.from("support-attachments").upload(path, attachment, {
        contentType: attachment.type,
        upsert: false,
      });

      if (uploadError) {
        setSubmitting(false);
        toast.error(uploadError.message);
        return;
      }

      attachmentPaths = [path];
    }

    const { data, error } = await supabase
      .from("support_tickets")
      .insert({
        user_id: user.id,
        account_role: deriveAccountRoleContext({
          isParent,
          householdOwnerId: household?.owner_id,
          userId: user.id,
        }),
        category: parsed.data.category,
        subject: parsed.data.subject,
        message: parsed.data.message,
        priority: parsed.data.priority,
        course_id: parsed.data.courseId || null,
        lesson_id: parsed.data.lessonId || null,
        module_key: parsed.data.moduleKey || null,
        household_id: household?.id || null,
        browser: getBrowserLabel(window.navigator.userAgent),
        device_type: getDeviceType(window.innerWidth),
        page_path: location.pathname,
        attachment_urls: attachmentPaths,
        metadata: {
          submitted_at: new Date().toISOString(),
          current_url: window.location.href,
          referrer: document.referrer || null,
          user_agent: window.navigator.userAgent,
          viewport: {
            width: window.innerWidth,
            height: window.innerHeight,
          },
          attachment_names: attachment ? [attachment.name] : [],
        },
      })
      .select("id")
      .single();

    if (error || !data) {
      setSubmitting(false);
      toast.error(error?.message || "Unable to submit your support request.");
      return;
    }

    setSubmitting(false);
    setAttachment(null);
    setFormState({
      category: formState.category,
      subject: "",
      message: "",
      priority: "normal",
      courseId: "",
      lessonId: "",
      moduleKey: "",
    });
    setSuccessReference(buildReferenceCode(data.id));
    toast.success("Support request submitted.");
    await loadTickets();
  };

  return (
    <PageShell bare>
      <header className="sticky top-0 z-40 border-b border-border bg-card/80 backdrop-blur-md">
        <div className="container mx-auto flex max-w-6xl items-center gap-3 px-4 py-4">
          <div>
            <p className="text-xs uppercase tracking-[0.22em] text-muted-foreground">HeirWise care</p>
            <h1 className="font-heading text-xl font-bold text-foreground">Help & Support</h1>
          </div>
        </div>
      </header>

      <main className="container mx-auto max-w-6xl space-y-6 px-4 py-6">
        <HelpCenterSection />

        <section className="glass-card p-6">
          <div className="grid gap-6 lg:grid-cols-[1.1fr,0.9fr] lg:items-center">
            <div className="space-y-4">
              <p className="text-sm uppercase tracking-[0.22em] text-muted-foreground">Support center</p>
              <h2 className="font-heading text-4xl font-bold text-foreground">Get clear help without leaving the learning flow.</h2>
              <p className="max-w-2xl text-sm leading-7 text-muted-foreground">
                Reach HeirWise support for technical issues, billing questions, account problems, family setup help, and lesson-related bugs with full context already attached.
              </p>
              <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                <span className="rounded-full border border-border/70 bg-background/60 px-3 py-1">Ticket tracking</span>
                <span className="rounded-full border border-border/70 bg-background/60 px-3 py-1">Request history</span>
                <span className="rounded-full border border-border/70 bg-background/60 px-3 py-1">FAQ quick help</span>
              </div>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              {SUPPORT_ACTIONS.map((action) => (
                <button
                  key={action.title}
                  type="button"
                  onClick={() => handleAction(action.category)}
                  className="rounded-3xl border border-border/70 bg-background/55 p-4 text-left transition-transform duration-200 hover:-translate-y-0.5 hover:border-primary/30 hover:bg-card"
                >
                  <p className="text-sm font-semibold text-foreground">{action.title}</p>
                  <p className="mt-2 text-xs leading-6 text-muted-foreground">{action.description}</p>
                </button>
              ))}
            </div>
          </div>
        </section>

        <div className="grid gap-6 xl:grid-cols-[1.05fr,0.95fr]">
          <section className="space-y-6">
            <div ref={formRef} className="glass-card p-6">
              <div className="mb-5 flex items-start gap-3">
                <div className="grid h-11 w-11 place-items-center rounded-2xl border border-primary/30 bg-primary/10 text-primary">
                  <LifeBuoy className="h-4 w-4" />
                </div>
                <div>
                  <h2 className="font-heading text-2xl font-bold text-foreground">Contact support</h2>
                  <p className="mt-1 text-sm text-muted-foreground">Tell us what happened and we’ll store the right product context automatically.</p>
                </div>
              </div>

              {successReference && (
                <div className="mb-5 rounded-3xl border border-primary/30 bg-primary/10 p-5">
                  <div className="flex items-start gap-3">
                    <CircleCheckBig className="mt-0.5 h-5 w-5 text-primary" />
                    <div>
                      <p className="text-sm font-semibold text-foreground">Support request received</p>
                      <p className="mt-1 text-sm text-muted-foreground">
                        Your request is now in the queue. Reference <span className="font-semibold text-foreground">{successReference}</span> appears below in My Support Requests.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <form className="space-y-4" onSubmit={handleSubmit}>
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="support-category">Issue category</Label>
                    <Select value={formState.category} onValueChange={(value) => setFormState((current) => ({ ...current, category: value as SupportCategory }))}>
                      <SelectTrigger id="support-category">
                        <SelectValue placeholder="Choose a category" />
                      </SelectTrigger>
                      <SelectContent>
                        {supportCategoryOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="support-priority">Priority</Label>
                    <Select value={formState.priority} onValueChange={(value) => setFormState((current) => ({ ...current, priority: value }))}>
                      <SelectTrigger id="support-priority">
                        <SelectValue placeholder="Choose priority" />
                      </SelectTrigger>
                      <SelectContent>
                        {supportPriorityOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="support-subject">Short subject</Label>
                  <Input
                    id="support-subject"
                    value={formState.subject}
                    onChange={(event) => setFormState((current) => ({ ...current, subject: event.target.value }))}
                    placeholder="Example: Parent dashboard billing page won’t load"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="support-message">Detailed message</Label>
                  <Textarea
                    id="support-message"
                    value={formState.message}
                    onChange={(event) => setFormState((current) => ({ ...current, message: event.target.value }))}
                    placeholder="What happened, what you expected, and what you already tried."
                    className="min-h-[160px]"
                    required
                  />
                </div>

                <div className="grid gap-4 md:grid-cols-3">
                  <div className="space-y-2">
                    <Label htmlFor="support-course">Course reference</Label>
                    <Input id="support-course" value={formState.courseId} onChange={(event) => setFormState((current) => ({ ...current, courseId: event.target.value }))} placeholder="Optional course" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="support-lesson">Lesson reference</Label>
                    <Input id="support-lesson" value={formState.lessonId} onChange={(event) => setFormState((current) => ({ ...current, lessonId: event.target.value }))} placeholder="Optional lesson" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="support-module">Module reference</Label>
                    <Input id="support-module" value={formState.moduleKey} onChange={(event) => setFormState((current) => ({ ...current, moduleKey: event.target.value }))} placeholder="Optional module" />
                  </div>
                </div>

                <div className="rounded-3xl border border-border/70 bg-background/55 p-4">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm font-semibold text-foreground">Screenshot or attachment</p>
                      <p className="text-xs text-muted-foreground">Optional. Add one file to help the team reproduce the issue faster.</p>
                    </div>
                    <Label htmlFor="support-attachment" className="inline-flex cursor-pointer items-center gap-2 rounded-2xl border border-border/70 bg-card px-4 py-2 text-sm font-medium text-foreground hover:border-primary/30">
                      <Upload className="h-4 w-4" />
                      {attachment ? "Replace file" : "Add file"}
                    </Label>
                  </div>
                  <Input
                    id="support-attachment"
                    type="file"
                    className="hidden"
                    onChange={(event) => setAttachment(event.target.files?.[0] || null)}
                  />
                  {attachment && <p className="mt-3 text-xs text-muted-foreground">Attached: {attachment.name}</p>}
                </div>

                <div className="rounded-3xl border border-border/70 bg-background/55 p-4 text-xs leading-6 text-muted-foreground">
                  We automatically attach page path, device type, browser family, timestamp, and account role so you don’t have to retype product context.
                </div>

                <Button type="submit" disabled={submitting}>
                  {submitting ? "Submitting request..." : "Submit support request"}
                </Button>
              </form>
            </div>

            <div className="glass-card p-6">
              <div className="mb-4 flex items-center gap-3">
                <div className="grid h-11 w-11 place-items-center rounded-2xl border border-accent/30 bg-accent/10 text-accent">
                  <HelpCircle className="h-4 w-4" />
                </div>
                <div>
                  <h2 className="font-heading text-2xl font-bold text-foreground">FAQ & quick help</h2>
                  <p className="text-sm text-muted-foreground">Search common HeirWise issues before opening a ticket.</p>
                </div>
              </div>

              <div className="relative mb-4">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={faqQuery}
                  onChange={(event) => setFaqQuery(event.target.value)}
                  placeholder="Search login, billing, family setup, AI features..."
                  className="pl-10"
                />
              </div>

              <div className="space-y-3">
                {faqResults.length === 0 ? (
                  <div className="rounded-2xl border border-border/70 bg-background/55 p-4 text-sm text-muted-foreground">No quick help matches yet. Submit a support request and we’ll take it from there.</div>
                ) : (
                  faqResults.map((item) => (
                    <div key={item.question} className="rounded-2xl border border-border/70 bg-background/55 p-4">
                      <p className="text-sm font-semibold text-foreground">{item.question}</p>
                      <p className="mt-2 text-sm leading-6 text-muted-foreground">{item.answer}</p>
                    </div>
                  ))
                )}
              </div>
            </div>
          </section>

          <aside className="space-y-6">
            <div className="glass-card p-6">
              <div className="mb-4 flex items-center gap-3">
                <div className="grid h-11 w-11 place-items-center rounded-2xl border border-secondary/30 bg-secondary/10 text-secondary">
                  <Sparkles className="h-4 w-4" />
                </div>
                <div>
                  <h2 className="font-heading text-2xl font-bold text-foreground">My support requests</h2>
                  <p className="text-sm text-muted-foreground">Track open tickets, status changes, and the latest visible updates.</p>
                </div>
              </div>

              <div className="space-y-3">
                {loadingTickets ? (
                  <div className="rounded-2xl border border-border/70 bg-background/55 p-4 text-sm text-muted-foreground">Loading your requests...</div>
                ) : tickets.length === 0 ? (
                  <div className="rounded-2xl border border-border/70 bg-background/55 p-4 text-sm text-muted-foreground">No support requests yet. When you submit one, it will appear here with its reference code and status.</div>
                ) : (
                  tickets.map((ticket) => {
                    const latestUpdate = latestUpdates[ticket.id];
                    return (
                      <div key={ticket.id} className="rounded-3xl border border-border/70 bg-background/55 p-4">
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                              <p className="text-sm font-semibold text-foreground">{ticket.subject}</p>
                              <span className={cn("rounded-full border px-2.5 py-1 text-[11px] font-medium", statusStyles[ticket.status])}>
                                {SUPPORT_STATUS_LABELS[ticket.status]}
                              </span>
                            </div>
                            <p className="mt-1 text-xs text-muted-foreground">{buildReferenceCode(ticket.id)} · {new Date(ticket.created_at).toLocaleDateString()}</p>
                          </div>
                          <div className="text-right text-xs text-muted-foreground">
                            <p>{supportCategoryOptions.find((option) => option.value === ticket.category)?.label || ticket.category}</p>
                            <p className="mt-1 capitalize">Priority: {ticket.priority.replace(/_/g, " ")}</p>
                          </div>
                        </div>

                        <div className="mt-3 rounded-2xl border border-border/60 bg-card/50 p-3 text-sm text-muted-foreground">
                          {latestUpdate?.message || `Latest update: ${SUPPORT_STATUS_LABELS[latestUpdate?.status || ticket.status]}`}
                        </div>

                        <div className="mt-3 flex flex-wrap gap-2 text-[11px] text-muted-foreground">
                          {ticket.page_path && <span className="rounded-full border border-border/70 bg-card px-2.5 py-1">Page: {ticket.page_path}</span>}
                          {ticket.attachment_urls.length > 0 && <span className="rounded-full border border-border/70 bg-card px-2.5 py-1">{ticket.attachment_urls.length} attachment{ticket.attachment_urls.length === 1 ? "" : "s"}</span>}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            <div className="glass-card p-6 space-y-4">
              <div className="mb-4 flex items-center gap-3">
                <div className="grid h-11 w-11 place-items-center rounded-2xl border border-primary/30 bg-primary/10 text-primary">
                  <CircleAlert className="h-4 w-4" />
                </div>
                <div>
                  <h2 className="font-heading text-2xl font-bold text-foreground">Need something else?</h2>
                  <p className="text-sm text-muted-foreground">For ideas, product suggestions, or curriculum requests, use the dedicated feedback flow.</p>
                </div>
              </div>
              <Button className="w-full" onClick={() => navigate("/feedback")}>Open Feedback Center</Button>
              <div className="rounded-xl border border-border/60 bg-card/40 p-3 text-center">
                <p className="text-xs text-muted-foreground">
                  You can also reach us directly at{" "}
                  <a href="mailto:founder@steenventures.com" className="text-primary hover:underline font-medium">
                    founder@steenventures.com
                  </a>
                </p>
              </div>
            </div>
          </aside>
        </div>
      </main>
    </PageShell>
  );
};

export default SupportCenter;
